/* ============================================================
   FIELD SCHEMA + dotted-path utilities — the spec the section
   editor renders from. Every editable value is addressed by a
   dotted path (e.g. 'philosophy.body1', 'disciplines.blocks.2.items.1.note').
   The owner edits content, never layout, so the design can't break.
   Ported from the prototype's FIELD_SCHEMA.
   ============================================================ */

export const FIELD_SCHEMA = {
  hero: [
    { group: 'Eyebrow', fields: [{ p: 'hero.label', l: 'Mono label', t: 'text' }] },
    {
      group: 'Headline',
      fields: [
        { p: 'hero.pre', l: 'Headline — before emphasis', t: 'text' },
        { p: 'hero.em', l: 'Emphasised word (serif · sage)', t: 'text' },
        { p: 'hero.post', l: 'Headline — after emphasis', t: 'text' },
      ],
    },
    { group: 'Sub line', fields: [{ p: 'hero.sub', l: 'Subheading', t: 'area' }] },
  ],
  philosophy: [
    {
      group: 'Headline',
      fields: [
        { p: 'philosophy.pre', l: 'Before emphasis', t: 'text' },
        { p: 'philosophy.em', l: 'Emphasised word', t: 'text' },
        { p: 'philosophy.post', l: 'After emphasis', t: 'text' },
      ],
    },
    { group: 'Pull-quote', fields: [{ p: 'philosophy.quote', l: 'Serif pull-quote', t: 'area' }] },
    {
      group: 'Body',
      fields: [
        { p: 'philosophy.body1', l: 'Paragraph one', t: 'area' },
        { p: 'philosophy.body2', l: 'Paragraph two', t: 'area' },
        { p: 'philosophy.caption', l: 'Image caption', t: 'text' },
      ],
    },
    { group: 'Image', fields: [{ p: 'philosophy.image', l: 'Quiet image', t: 'image' }] },
  ],
  method: [
    {
      group: 'Headline',
      fields: [
        { p: 'method.pre', l: 'Before emphasis', t: 'text' },
        { p: 'method.em', l: 'Emphasised word', t: 'text' },
        { p: 'method.post', l: 'After emphasis', t: 'text' },
      ],
    },
    {
      group: 'Scenes',
      fields: [{ p: 'method.overline', l: 'Gallery overline', t: 'text' }],
      scenes: true,
    },
    {
      group: 'Statement',
      fields: [
        { p: 'method.statement', l: 'Serif statement', t: 'area' },
        { p: 'method.subtext', l: 'Mono subtext', t: 'text' },
      ],
    },
  ],
  disciplines: [
    {
      group: 'Headline',
      fields: [
        { p: 'disciplines.pre', l: 'Before emphasis', t: 'text' },
        { p: 'disciplines.em', l: 'Emphasised word', t: 'text' },
        { p: 'disciplines.post', l: 'After emphasis', t: 'text' },
      ],
    },
    { group: 'Register', blocks: true, fields: [] },
  ],
  engagements: [
    {
      group: 'Headline',
      fields: [
        { p: 'engagements.pre', l: 'Before emphasis', t: 'text' },
        { p: 'engagements.em', l: 'Emphasised word', t: 'text' },
        { p: 'engagements.post', l: 'After emphasis', t: 'text' },
      ],
    },
    {
      group: 'Three engagements',
      fields: [
        { p: 'engagements.three.0.title', l: '01 · Title', t: 'text' },
        { p: 'engagements.three.0.text', l: '01 · Text', t: 'area' },
        { p: 'engagements.three.1.title', l: '02 · Title', t: 'text' },
        { p: 'engagements.three.1.text', l: '02 · Text', t: 'area' },
        { p: 'engagements.three.2.title', l: '03 · Title', t: 'text' },
        { p: 'engagements.three.2.text', l: '03 · Text', t: 'area' },
      ],
    },
    {
      group: 'Statement',
      fields: [{ p: 'engagements.statement', l: 'Serif statement', t: 'area' }],
    },
    {
      group: 'Houses we keep',
      fields: [
        { p: 'engagements.houses.0', l: 'Line one', t: 'text' },
        { p: 'engagements.houses.1', l: 'Line two', t: 'text' },
        { p: 'engagements.houses.2', l: 'Line three', t: 'text' },
        { p: 'engagements.houses.3', l: 'Line four', t: 'text' },
      ],
    },
  ],
  difference: [
    {
      group: 'Headline',
      fields: [
        { p: 'difference.pre', l: 'Before emphasis', t: 'text' },
        { p: 'difference.em', l: 'Emphasised word', t: 'text' },
        { p: 'difference.post', l: 'After emphasis', t: 'text' },
      ],
    },
    {
      group: 'Comparison rows',
      fields: [
        { p: 'difference.rows.0.a', l: 'Agency 1', t: 'text' },
        { p: 'difference.rows.0.b', l: 'Advisory 1', t: 'text' },
        { p: 'difference.rows.1.a', l: 'Agency 2', t: 'text' },
        { p: 'difference.rows.1.b', l: 'Advisory 2', t: 'text' },
        { p: 'difference.rows.2.a', l: 'Agency 3', t: 'text' },
        { p: 'difference.rows.2.b', l: 'Advisory 3', t: 'text' },
        { p: 'difference.rows.3.a', l: 'Agency 4', t: 'text' },
        { p: 'difference.rows.3.b', l: 'Advisory 4', t: 'text' },
        { p: 'difference.rows.4.a', l: 'Agency 5', t: 'text' },
        { p: 'difference.rows.4.b', l: 'Advisory 5', t: 'text' },
      ],
    },
    {
      group: 'Statement',
      fields: [{ p: 'difference.statement', l: 'Serif statement', t: 'area' }],
    },
  ],
  enquiry: [
    {
      group: 'Headline',
      fields: [
        { p: 'enquiry.pre', l: 'Before emphasis', t: 'text' },
        { p: 'enquiry.em', l: 'Emphasised word', t: 'text' },
        { p: 'enquiry.post', l: 'After emphasis', t: 'text' },
      ],
    },
    {
      group: 'Intro',
      fields: [
        { p: 'enquiry.lead', l: 'Lead line', t: 'area' },
        { p: 'enquiry.overline', l: 'Panel overline', t: 'text' },
        { p: 'enquiry.title', l: 'Panel title', t: 'text' },
      ],
    },
    {
      group: 'The matter (cards)',
      fields: [
        { p: 'enquiry.matters.0', l: 'Card 1', t: 'text' },
        { p: 'enquiry.matters.1', l: 'Card 2', t: 'text' },
        { p: 'enquiry.matters.2', l: 'Card 3', t: 'text' },
        { p: 'enquiry.matters.3', l: 'Card 4', t: 'text' },
      ],
    },
    {
      group: 'The horizon (chips)',
      fields: [
        { p: 'enquiry.horizons.0', l: 'Chip 1', t: 'text' },
        { p: 'enquiry.horizons.1', l: 'Chip 2', t: 'text' },
        { p: 'enquiry.horizons.2', l: 'Chip 3', t: 'text' },
      ],
    },
  ],
}

// Immutable get/set by dotted path (ported from the prototype).
export function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)
}

export function setPath(obj, path, value) {
  const keys = path.split('.')
  const clone = Array.isArray(obj) ? obj.slice() : { ...obj }
  let cur = clone
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    const next = cur[k]
    cur[k] = Array.isArray(next) ? next.slice() : { ...next }
    cur = cur[k]
  }
  cur[keys[keys.length - 1]] = value
  return clone
}

export function deepClone(o) {
  return JSON.parse(JSON.stringify(o))
}
